/**
 * Vuex модуль для централизованного управления WebSocket подписками
 *
 * Best Practice: Подписки создаются ОДИН РАЗ на уровне store, а не в компонентах
 * Это решает проблему дублирования подписок при hot-reload
 *
 * ВАЖНО: Этот модуль НЕ должен персиститься в localStorage!
 * WebSocket подписки закрываются при закрытии страницы и должны пересоздаваться при каждой загрузке.
 * См. store.js - модуль исключен через reducer в createPersistedState()
 *
 * FT-066: мигрирован с legacy-обёртки ($cable) на нативный @rails/actioncable
 * cable-сервис. Subscription-ОБЪЕКТЫ держим в module-scoped реестре `bootSubs` (НЕ в Vuex
 * state — они несериализуемы и не должны попадать в persist). Vuex state хранит только
 * idempotency-флаги/ID. received-хендлеры висят на объекте подписки и замыкаются на
 * commit/rootGetters → диспатчат store-мутации (component context не нужен для этих 3 каналов).
 *
 * Владение каналами (FT-066 r4):
 *   - boot-модуль (здесь): HeaderUserChannel, CompanyChannel, MainCompanyChannel — чистый
 *     store-dispatch, без component context.
 *   - AppHeader.vue: AppearanceChannel (perform), NewBookingChannel (звук/роутер),
 *     TeletypeChannel (звук/роутер) — их received требует компонентного контекста.
 */
import { subscribe } from "@/cable";

// Module-scoped реестр живых subscription-объектов (НЕ Vuex state: несериализуемы).
let bootSubs = {};

// received-хендлеры (замыкание на commit): диспатчат root-мутации, перенесённые 1:1 из
// AppHeader channels:{} блоков (HeaderUser/Company/MainCompany).
function handleHeaderUser(commit, dispatch, data) {
  if (data && data.type === "new ticket") {
    commit("setHasNewTicket", true, { root: true });
  }
  if (data && data.type === 'telephony_configuration') {
    dispatch('incomingCalls/configurationChanged', data, { root: true });
  }
}
function handleCompany(commit, data) {
  // AppHeader-блок коммитил безусловно: setCompany({ company: data.company }).
  commit("setCompany", { company: data.company }, { root: true });
}
function handleMainCompany(commit, data) {
  // AppHeader-блок коммитил безусловно: setMainCompany(data.main_company).
  commit("setMainCompany", data.main_company, { root: true });
}

const state = {
  // Флаг инициализации подписок (для защиты от повторной подписки)
  // НЕ персистится - всегда false при загрузке страницы
  subscriptionsInitialized: false,

  // Сохраненные ID для безопасной отписки
  subscribedCompanyId: null,
  subscribedMainCompanyId: null,
  subscribedUserId: null,

  // Активные подписки (для отслеживания)
  activeSubscriptions: []
};

const getters = {
  isSubscribed: (state) => state.subscriptionsInitialized,
  subscribedCompanyId: (state) => state.subscribedCompanyId,
  subscribedUserId: (state) => state.subscribedUserId,
};

const mutations = {
  SET_SUBSCRIPTIONS_INITIALIZED(state, value) {
    state.subscriptionsInitialized = value;
  },

  SET_SUBSCRIBED_IDS(state, { companyId, mainCompanyId, userId }) {
    state.subscribedCompanyId = companyId;
    state.subscribedMainCompanyId = mainCompanyId;
    state.subscribedUserId = userId;
  },

  ADD_ACTIVE_SUBSCRIPTION(state, subscription) {
    state.activeSubscriptions.push(subscription);
  },

  CLEAR_ACTIVE_SUBSCRIPTIONS(state) {
    state.activeSubscriptions = [];
  }
};

const actions = {
  /**
   * Инициализация всех WebSocket подписок
   * Вызывается ОДИН РАЗ при загрузке приложения
   *
   * FT-066: подписывает только store-only каналы (HeaderUser/Company/MainCompany).
   * AppearanceChannel/NewBookingChannel/TeletypeChannel живут в AppHeader (component context).
   */
  initializeSubscriptions({ commit, dispatch, state, rootGetters }, { userRole }) {
    // Защита от повторной инициализации
    if (state.subscriptionsInitialized) {
      console.log("WebSocket: Subscriptions already initialized, skipping");
      return;
    }

    // Структура: state.company = { company: {...} }
    // Поэтому getCompany возвращает { company: {...} }, а не сам объект компании
    const companyWrapper = rootGetters.getCompany;
    const company = companyWrapper?.company;
    const mainCompany = rootGetters.getMainCompany;
    const userId = rootGetters.currentUserId;

    if (!company || !company.id) {
      console.warn("WebSocket: Cannot initialize - company not loaded");
      return;
    }

    console.log("WebSocket: 🚀 Initializing subscriptions...");

    // Сохраняем ID для последующей отписки
    commit('SET_SUBSCRIBED_IDS', {
      companyId: company.id,
      mainCompanyId: mainCompany?.id,
      userId: userId
    });

    // userRole больше не гейтит каналы здесь: gated NewBooking/Teletype переехали в AppHeader.
    // Принимаем аргумент для совместимости с двумя call-sites и возможных будущих gated-каналов.
    void userRole;

    // 1. HeaderUserChannel - обновления пользователя в header (new ticket badge)
    bootSubs.headerUser = subscribe(
      { channel: "HeaderUserChannel", room: userId, user_id: userId },
      {
        received: (data) => handleHeaderUser(commit, dispatch, data),
        connected: () => dispatch('incomingCalls/configurationChanged', null, { root: true }),
      },
    );
    commit('ADD_ACTIVE_SUBSCRIPTION', { channel: 'HeaderUserChannel', room: userId });

    // 2. MainCompanyChannel - обновления главной компании
    if (mainCompany?.id) {
      bootSubs.mainCompany = subscribe(
        { channel: "MainCompanyChannel", room: mainCompany.id },
        { received: (data) => handleMainCompany(commit, data) },
      );
      commit('ADD_ACTIVE_SUBSCRIPTION', { channel: 'MainCompanyChannel', room: mainCompany.id });
    }

    // 3. CompanyChannel - обновления компании
    bootSubs.company = subscribe(
      { channel: "CompanyChannel", room: company.id },
      { received: (data) => handleCompany(commit, data) },
    );
    commit('ADD_ACTIVE_SUBSCRIPTION', { channel: 'CompanyChannel', room: company.id });

    // Устанавливаем флаг инициализации
    commit('SET_SUBSCRIPTIONS_INITIALIZED', true);

    console.log("WebSocket: ✅ Subscriptions initialized successfully", state.activeSubscriptions.length, "channels");
  },

  /**
   * Идемпотентная до-подписка на MainCompanyChannel.
   * initializeSubscriptions подписывает его только если main_company.id уже загружен.
   * Но main_company часто доезжает ПОЗЖЕ company (recovery-фетч в AppHeader асинхронный) —
   * тогда основной init проходит без MainCompanyChannel («2 channels»). Этот action
   * вызывается watcher'ом на getMainCompany.id и до-подписывает канал, когда id появился.
   * Безопасен к повторам: гейт по subscriptionsInitialized + subscribedMainCompanyId.
   */
  ensureMainCompanyChannel({ commit, state, rootGetters }) {
    if (!state.subscriptionsInitialized) return; // основного init ещё не было — он сам подпишет
    if (state.subscribedMainCompanyId) return;   // уже подписан
    const mainCompany = rootGetters.getMainCompany;
    if (!mainCompany || !mainCompany.id) return;

    bootSubs.mainCompany = subscribe(
      { channel: "MainCompanyChannel", room: mainCompany.id },
      { received: (data) => handleMainCompany(commit, data) },
    );
    commit('ADD_ACTIVE_SUBSCRIPTION', { channel: 'MainCompanyChannel', room: mainCompany.id });
    // Сохраняем id для корректной отписки в unsubscribeAll (companyId/userId не трогаем).
    commit('SET_SUBSCRIBED_IDS', {
      companyId: state.subscribedCompanyId,
      mainCompanyId: mainCompany.id,
      userId: state.subscribedUserId,
    });
    console.log("WebSocket: ✅ MainCompanyChannel subscribed (deferred)", state.activeSubscriptions.length, "channels");
  },

  /**
   * Отписка от всех каналов
   * Вызывается при logout или смене филиала
   *
   * FT-066: отписываемся по held-ОБЪЕКТАМ из bootSubs (нативный sub.unsubscribe()),
   * а не по строковым именам канала (как требовала старая name-keyed обёртка).
   */
  unsubscribeAll({ commit, state }) {
    if (!state.subscriptionsInitialized) {
      console.log("WebSocket: Already unsubscribed, skipping");
      return;
    }

    console.log("WebSocket: 🔌 Unsubscribing from all channels...");

    Object.values(bootSubs).forEach((s) => s && s.unsubscribe());
    bootSubs = {};

    // Сбрасываем состояние
    commit('SET_SUBSCRIPTIONS_INITIALIZED', false);
    commit('SET_SUBSCRIBED_IDS', { companyId: null, mainCompanyId: null, userId: null });
    commit('CLEAR_ACTIVE_SUBSCRIPTIONS');

    console.log("WebSocket: ✅ All subscriptions closed");
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
